import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Blocco "modifiche non salvate". L'app usa <BrowserRouter> (non un data
// router), quindi useBlocker non e' disponibile: si intercettano push() e go()
// del navigator del router, che passano da qualunque Link/NavLink/navigate().
// replace() resta libero (redirect di sessione scaduta, pulizia dei parametri).
// Il tasto Indietro del browser non e' bloccabile; chiusura/ricarica della
// scheda sono coperte da beforeunload.
const UnsavedChangesContext = createContext(null);

const targetPathname = (to) => {
  if (typeof to === "string") return to.split(/[?#]/)[0];
  if (to && typeof to === "object") return to.pathname;
  return null;
};

export function UnsavedChangesProvider({ children }) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const dirtyKeys = useRef(new Set());
  const [pending, setPending] = useState(null);

  const setDirty = useCallback((key, dirty) => {
    if (dirty) dirtyKeys.current.add(key);
    else dirtyKeys.current.delete(key);
  }, []);

  useEffect(() => {
    const { push, go } = navigator;
    navigator.push = (to, ...rest) => {
      const samePage = targetPathname(to) === window.location.pathname;
      if (!dirtyKeys.current.size || samePage) return push.call(navigator, to, ...rest);
      setPending({ proceed: () => push.call(navigator, to, ...rest) });
    };
    navigator.go = (delta) => {
      if (!dirtyKeys.current.size) return go.call(navigator, delta);
      setPending({ proceed: () => go.call(navigator, delta) });
    };
    return () => {
      navigator.push = push;
      navigator.go = go;
    };
  }, [navigator]);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!dirtyKeys.current.size) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const value = useMemo(() => ({ setDirty }), [setDirty]);

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <AlertDialog open={!!pending} onOpenChange={(open) => { if (!open) setPending(null); }}>
        <AlertDialogContent data-testid="unsaved-changes-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-extrabold">Modifiche non salvate</AlertDialogTitle>
            <AlertDialogDescription>
              Hai modificato delle impostazioni senza salvarle. Se esci ora le modifiche andranno perse.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="unsaved-stay">Resta qui</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-full"
              data-testid="unsaved-leave"
              onClick={() => {
                const go = pending?.proceed;
                setPending(null);
                dirtyKeys.current.clear();
                go?.();
              }}
            >
              Esci senza salvare
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </UnsavedChangesContext.Provider>
  );
}

// Segnala al provider che la pagina ha modifiche non salvate. `key` distingue
// piu' sorgenti nella stessa pagina; al dismount la segnalazione si ritira.
export function useUnsavedChangesGuard(dirty, key = "page") {
  const ctx = useContext(UnsavedChangesContext);
  useEffect(() => {
    if (!ctx) return undefined;
    ctx.setDirty(key, Boolean(dirty));
    return () => ctx.setDirty(key, false);
  }, [ctx, dirty, key]);
}
